import {
  getCatalogSourceLabel,
  getLocalizedFoodName,
  getResolvedFoodType,
  isCatalogFood,
} from "./foodCatalog";

function hasText(value) {
  return typeof value === "string" && value.trim().length > 0;
}

function getFiniteNumber(value, fallback = 0) {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : fallback;
}

function getFoodMicros(food) {
  if (food?.micros_per_100g && typeof food.micros_per_100g === "object") {
    return food.micros_per_100g;
  }

  if (food?.micros && typeof food.micros === "object") {
    return food.micros;
  }

  if (food?.nutrients && typeof food.nutrients === "object") {
    return food.nutrients;
  }

  return {};
}

function getFoodReferenceQuantityG(food) {
  return getFiniteNumber(
    food?.quantityReferenceG ?? food?.reference_quantity_g ?? food?.quantity,
    100,
  );
}

function getFoodNutrientsPer100g(food) {
  const nutrientsPer100g = Array.isArray(food?.nutrients_per_100g)
    ? food.nutrients_per_100g
    : Array.isArray(food?.nutrientsPer100g)
      ? food.nutrientsPer100g
      : null;

  if (!nutrientsPer100g || nutrientsPer100g.length === 0) {
    return null;
  }

  return nutrientsPer100g.reduce(
    (nutrients, entry) => {
      const nutrientId = String(entry?.id ?? "").trim();
      if (!nutrientId) {
        return nutrients;
      }

      nutrients[nutrientId] = getFiniteNumber(entry?.value);
      return nutrients;
    },
    {
      calories: 0,
      carbs: 0,
      protein: 0,
      fats: 0,
      fiber: 0,
    },
  );
}

function getFoodNutrients(food) {
  const nutrientsPer100g = getFoodNutrientsPer100g(food);
  if (nutrientsPer100g) {
    return nutrientsPer100g;
  }

  const micros = getFoodMicros(food);

  return {
    calories: getFiniteNumber(food?.calories),
    carbs: getFiniteNumber(food?.carbs),
    protein: getFiniteNumber(food?.protein),
    fats: getFiniteNumber(food?.fats),
    fiber: getFiniteNumber(food?.fiber),
    ...micros,
  };
}

function getFoodSourceLabel(food, t) {
  if (isCatalogFood(food)) {
    return getCatalogSourceLabel(food, t);
  }

  return typeof t === "function" ? t("createdByMe-string") : "Created by me";
}

function getFoodSearchText(food, language, sourceLabel) {
  return [
    getLocalizedFoodName(food, language),
    food?.name,
    food?.name_es,
    food?.name_en,
    food?.brand,
    food?.store,
    food?.per,
    food?.notes,
    sourceLabel,
    food?.source,
  ]
    .filter(hasText)
    .join(" ")
    .toLowerCase();
}

export function normalizeFoodBrowserItem(food, { language = "en", t } = {}) {
  if (!food || typeof food !== "object") {
    return null;
  }

  const name = getLocalizedFoodName(food, language).trim();
  const sourceLabel = getFoodSourceLabel(food, t);

  return {
    id: String(food?.id ?? food?.code ?? name).trim(),
    sourceKind: isCatalogFood(food) ? "catalog" : "coach",
    sourceLabel,
    type: getResolvedFoodType(food),
    name,
    brand: hasText(food?.brand) ? food.brand.trim() : "",
    store: hasText(food?.store) ? food.store.trim() : "",
    referenceQuantityG: getFoodReferenceQuantityG(food),
    nutrients: getFoodNutrients(food),
    searchText: getFoodSearchText(food, language, sourceLabel),
    sortName: name.toLowerCase(),
    sourceRecord: food,
  };
}

export function buildFoodBrowserItems(foods, options = {}) {
  return (Array.isArray(foods) ? foods : [])
    .map((food) => normalizeFoodBrowserItem(food, options))
    .filter(Boolean);
}

export function matchesFoodBrowserSearch(item, searchText) {
  const normalizedSearch = String(searchText ?? "")
    .trim()
    .toLowerCase();

  if (!normalizedSearch) {
    return true;
  }

  return String(item?.searchText ?? "").includes(normalizedSearch);
}

export function matchesFoodBrowserFilter(item, filterKey) {
  if (filterKey === "all-string") {
    return true;
  }

  if (filterKey === "createdByMe-string") {
    return item?.sourceKind === "coach";
  }

  if (filterKey === "food-string") {
    return item?.type === "food";
  }

  if (filterKey === "supplement-string") {
    return item?.type === "supplement";
  }

  return false;
}

export function sortFoodBrowserItems(items, sortField = null) {
  if (!sortField) {
    return items;
  }

  return [...items].sort((leftItem, rightItem) => {
    const leftValue = getFiniteNumber(leftItem?.nutrients?.[sortField]);
    const rightValue = getFiniteNumber(rightItem?.nutrients?.[sortField]);

    if (rightValue !== leftValue) {
      return rightValue - leftValue;
    }

    return String(leftItem?.sortName ?? "").localeCompare(
      String(rightItem?.sortName ?? ""),
      undefined,
      { sensitivity: "base" },
    );
  });
}

export function getFoodBrowserItemKey(item) {
  return item?.id || null;
}
